import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CheckIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CloseIcon from "@mui/icons-material/HighlightOffRounded";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import {
  getImportJobs,
  getImportJob,
  startImportJob,
  updateImportItem,
  updateImportItemVariant,
  approveImportItemsBulk,
  rejectImportItem,
  deleteImportJob,
} from "../../api/productImportService";
import { getStoreOptions } from "../../api/adminService";
import { getCategories, getCategoryAttributes } from "../../api/categoryService";
import { getStoreCategories } from "../../api/storeService";

// Matches the backend's ProductImportJobStatus enum (Models/Enums.cs).
const JOB_STATUS = { 1: "Pending", 2: "Fetching", 3: "Completed", 4: "Failed" };
const JOB_STATUS_COLOR = { 1: "default", 2: "info", 3: "success", 4: "error" };

// Matches the backend's ProductImportItemStatus enum.
const ITEM_STATUS = { 1: "Pending Review", 2: "Approved", 3: "Rejected", 4: "Created", 5: "Failed" };
const ITEM_STATUS_COLOR = { 1: "default", 2: "info", 3: "warning", 4: "success", 5: "error" };
const ITEM_STATUS_CREATED = 4;
const ITEM_STATUS_REJECTED = 3;

const ACTIVE_JOB_STATUSES = [1, 2];

// Leaf categories only, that the target store has enabled - mirrors the backend's
// ValidateCategoryAsync check (Services/ProductValidation.cs).
function buildCategoryOptions(categories, storeCategories) {
  const parentIds = new Set();
  const byId = new Map();
  categories.forEach((c) => {
    byId.set(c.categoryId, c);
    if (c.parentCategoryId != null) parentIds.add(c.parentCategoryId);
  });

  const pathFor = (categoryId) => {
    const parts = [];
    let current = byId.get(categoryId);
    while (current) {
      parts.unshift(current.name);
      current = current.parentCategoryId != null ? byId.get(current.parentCategoryId) : null;
    }
    return parts.join(" › ");
  };

  return storeCategories
    .filter((sc) => !parentIds.has(sc.categoryId))
    .map((sc) => ({ categoryId: sc.categoryId, name: pathFor(sc.categoryId) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export default function ProductImports() {
  const [jobs, setJobs] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [error, setError] = useState("");
  const [newImportOpen, setNewImportOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const reloadJobs = useCallback(async () => {
    const data = await getImportJobs();
    setJobs(data.items);
  }, []);

  const reloadSelectedJob = useCallback(async (jobId) => {
    if (!jobId) return;
    const job = await getImportJob(jobId);
    setSelectedJob(job);
  }, []);

  useEffect(() => {
    reloadJobs().catch(() => setError("Could not load import jobs. Is the API running?"));
    getStoreOptions()
      .then(setStores)
      .catch(() => setError("Could not load stores."));
  }, [reloadJobs]);

  useEffect(() => {
    if (selectedJobId) reloadSelectedJob(selectedJobId).catch(() => setError("Could not load this import job."));
  }, [selectedJobId, reloadSelectedJob]);

  const targetStoreId = selectedJob?.targetStoreId;
  useEffect(() => {
    if (!targetStoreId) {
      setCategoryOptions([]);
      return;
    }
    Promise.all([getCategories(), getStoreCategories(targetStoreId)])
      .then(([categories, storeCategories]) => setCategoryOptions(buildCategoryOptions(categories, storeCategories)))
      .catch(() => setError("Could not load categories for this store."));
  }, [targetStoreId]);

  // Poll while any job is still fetching, and refresh the open job's items as they arrive.
  useEffect(() => {
    const hasActiveJob = jobs.some((j) => ACTIVE_JOB_STATUSES.includes(j.status));
    const selectedIsActive = selectedJob && ACTIVE_JOB_STATUSES.includes(selectedJob.status);
    if (!hasActiveJob && !selectedIsActive) return undefined;

    const interval = setInterval(() => {
      reloadJobs().catch(() => {});
      if (selectedJobId) reloadSelectedJob(selectedJobId).catch(() => {});
    }, 2000);

    return () => clearInterval(interval);
  }, [jobs, selectedJob, selectedJobId, reloadJobs, reloadSelectedJob]);

  async function handleStartImport({ storeId, sourceUrl, isCollection }) {
    try {
      const job = await startImportJob({ storeId, sourceUrl, isCollection });
      setNewImportOpen(false);
      await reloadJobs();
      setSelectedJobId(job.productImportJobId);
    } catch (err) {
      setError(err.response?.data || "Could not start this import.");
    }
  }

  async function handleDeleteJob(job) {
    if (!window.confirm(`Delete this import job (${job.sourceUrl})? This cannot be undone.`)) return;
    try {
      await deleteImportJob(job.productImportJobId);
      if (selectedJobId === job.productImportJobId) {
        setSelectedJobId(null);
        setSelectedJob(null);
      }
      await reloadJobs();
    } catch (err) {
      setError(err.response?.data || "Could not delete this import job.");
    }
  }

  async function handleSaveItem(itemId, patch) {
    try {
      await updateImportItem(selectedJobId, itemId, patch);
      setEditingItem(null);
      await reloadSelectedJob(selectedJobId);
    } catch (err) {
      setError(err.response?.data || "Could not save this item.");
    }
  }

  async function handleSaveVariant(itemId, variantId, patch) {
    try {
      await updateImportItemVariant(selectedJobId, itemId, variantId, patch);
      const refreshed = await getImportJob(selectedJobId);
      setSelectedJob(refreshed);
      setEditingItem((current) =>
        current ? refreshed.items.find((i) => i.productImportItemId === current.productImportItemId) || null : null
      );
    } catch (err) {
      setError(err.response?.data || "Could not save this variant.");
    }
  }

  async function handleQuickCategoryChange(item, categoryId) {
    try {
      await updateImportItem(selectedJobId, item.productImportItemId, {
        reviewName: item.reviewName,
        reviewDescription: item.reviewDescription,
        reviewPrice: item.reviewPrice,
        reviewStockQuantity: item.reviewStockQuantity,
        reviewCategoryId: categoryId,
        selectedImageIds: item.images.filter((i) => i.isSelected).map((i) => i.productImportItemImageId),
        includeVideo: item.includeVideo,
      });
      await reloadSelectedJob(selectedJobId);
    } catch (err) {
      setError(err.response?.data || "Could not update the category.");
    }
  }

  async function handleReject(item) {
    try {
      await rejectImportItem(selectedJobId, item.productImportItemId);
      await reloadSelectedJob(selectedJobId);
    } catch (err) {
      setError(err.response?.data || "Could not reject this item.");
    }
  }

  async function handleApproveSelected() {
    if (selectedItemIds.length === 0) return;
    setBusy(true);
    try {
      await approveImportItemsBulk(selectedJobId, selectedItemIds);
      setSelectedItemIds([]);
      await reloadSelectedJob(selectedJobId);
    } catch (err) {
      setError(err.response?.data || "Could not create the selected products.");
    } finally {
      setBusy(false);
    }
  }

  function toggleItemSelected(itemId) {
    setSelectedItemIds((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]));
  }

  const items = selectedJob?.items || [];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Import Products</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewImportOpen(true)}>
          New Import
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : "Something went wrong."}
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Paper sx={{ width: 340, flexShrink: 0 }}>
          <List disablePadding>
            {jobs.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No imports yet.
              </Typography>
            )}
            {jobs.map((job, index) => (
              <React.Fragment key={job.productImportJobId}>
                {index > 0 && <Divider />}
                <ListItemButton
                  selected={selectedJobId === job.productImportJobId}
                  onClick={() => setSelectedJobId(job.productImportJobId)}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={JOB_STATUS[job.status]} color={JOB_STATUS_COLOR[job.status]} />
                        <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                          {job.targetStoreName}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" noWrap component="span" sx={{ display: "block" }}>
                        {job.sourceUrl} · {job.discoveredCount} found
                      </Typography>
                    }
                  />
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteJob(job);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        </Paper>

        <Paper sx={{ flexGrow: 1, minHeight: 400, p: selectedJob ? 0 : 3 }}>
          {!selectedJob && (
            <Typography variant="body2" color="text.secondary">
              Select an import on the left, or start a new one.
            </Typography>
          )}

          {selectedJob && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
                <Box>
                  <Typography variant="subtitle1">{selectedJob.targetStoreName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedJob.sourceUrl}
                  </Typography>
                  {selectedJob.status === 4 && selectedJob.errorMessage && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {selectedJob.errorMessage}
                    </Alert>
                  )}
                  {(selectedJob.status === 1 || selectedJob.status === 2) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Fetching products… this list will fill in as they're found.
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  startIcon={<CheckIcon />}
                  disabled={selectedItemIds.length === 0 || busy}
                  onClick={handleApproveSelected}
                >
                  Create Selected ({selectedItemIds.length})
                </Button>
              </Stack>

              {items.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Image</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Variants</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => {
                      const mainImage = item.images.find((i) => i.isSelected) || item.images[0];
                      const canApprove = item.status !== ITEM_STATUS_CREATED && item.status !== ITEM_STATUS_REJECTED;

                      return (
                        <TableRow key={item.productImportItemId} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              disabled={!canApprove}
                              checked={selectedItemIds.includes(item.productImportItemId)}
                              onChange={() => toggleItemSelected(item.productImportItemId)}
                            />
                          </TableCell>
                          <TableCell>
                            <Avatar variant="rounded" src={mainImage?.externalImageUrl} sx={{ width: 40, height: 40 }}>
                              <ImageOutlinedIcon fontSize="small" />
                            </Avatar>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="body2" noWrap>
                                {item.reviewName || <em>(no name extracted)</em>}
                              </Typography>
                              {item.extractedVideoUrl && (
                                <Tooltip title={item.includeVideo ? "Video will be included" : "Video found, excluded"}>
                                  <VideocamOutlinedIcon
                                    fontSize="inherit"
                                    color={item.includeVideo ? "primary" : "disabled"}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                            {item.errorMessage && (
                              <Typography variant="caption" color="error" noWrap component="div">
                                {item.errorMessage}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{item.reviewPrice ?? "—"}</TableCell>
                          <TableCell sx={{ minWidth: 180 }}>
                            <TextField
                              select
                              size="small"
                              fullWidth
                              value={item.reviewCategoryId ?? ""}
                              disabled={!canApprove}
                              onChange={(e) => handleQuickCategoryChange(item, Number(e.target.value))}
                            >
                              {categoryOptions.map((c) => (
                                <MenuItem key={c.categoryId} value={c.categoryId}>
                                  {c.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell>
                            {item.variants.length > 0 && (
                              <Chip
                                size="small"
                                icon={<StyleOutlinedIcon />}
                                label={`${item.variants.filter((v) => v.createdVariantId || v.reviewOptionId).length}/${item.variants.length} mapped`}
                                onClick={() => setEditingItem(item)}
                                color={item.variants.every((v) => v.createdVariantId || v.reviewOptionId) ? "success" : "warning"}
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={ITEM_STATUS[item.status]} color={ITEM_STATUS_COLOR[item.status]} />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Edit details">
                                <IconButton size="small" onClick={() => setEditingItem(item)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {canApprove && (
                                <Tooltip title="Reject">
                                  <IconButton size="small" onClick={() => handleReject(item)}>
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}

              {items.length === 0 && selectedJob.status === 3 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No products were found at this URL.
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Stack>

      {newImportOpen && (
        <NewImportDialog stores={stores} onClose={() => setNewImportOpen(false)} onStart={handleStartImport} />
      )}

      {editingItem && (
        <ItemEditDialog
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={handleSaveItem}
          onSaveVariant={(variantId, patch) => handleSaveVariant(editingItem.productImportItemId, variantId, patch)}
        />
      )}
    </Box>
  );
}

function NewImportDialog({ stores, onClose, onStart }) {
  const [storeId, setStoreId] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [isCollection, setIsCollection] = useState(false);

  const canStart = storeId && sourceUrl.trim();

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>New Product Import</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label="Store" value={storeId} onChange={(e) => setStoreId(e.target.value)} fullWidth>
            {stores.map((store) => (
              <MenuItem key={store.storeId} value={store.storeId}>
                {store.storeName}
              </MenuItem>
            ))}
          </TextField>

          <ToggleButtonGroup
            value={isCollection}
            exclusive
            onChange={(e, value) => value !== null && setIsCollection(value)}
            size="small"
            fullWidth
          >
            <ToggleButton value={false}>Single Product</ToggleButton>
            <ToggleButton value>Collection / Listing Page</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label="Product URL"
            placeholder="https://example.com/products/..."
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            fullWidth
            autoFocus
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canStart} onClick={() => onStart({ storeId, sourceUrl: sourceUrl.trim(), isCollection })}>
          Start Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ItemEditDialog({ item, onClose, onSave, onSaveVariant }) {
  const [reviewName, setReviewName] = useState(item.reviewName || "");
  const [reviewDescription, setReviewDescription] = useState(item.reviewDescription || "");
  const [reviewPrice, setReviewPrice] = useState(item.reviewPrice ?? "");
  const [reviewStockQuantity, setReviewStockQuantity] = useState(item.reviewStockQuantity ?? "");
  const [selectedImageIds, setSelectedImageIds] = useState(
    item.images.filter((i) => i.isSelected).map((i) => i.productImportItemImageId)
  );
  const [includeVideo, setIncludeVideo] = useState(item.includeVideo);
  const [variantAttributes, setVariantAttributes] = useState([]);
  const [variantAttributesError, setVariantAttributesError] = useState("");

  function toggleImage(imageId) {
    setSelectedImageIds((prev) => (prev.includes(imageId) ? prev.filter((id) => id !== imageId) : [...prev, imageId]));
  }

  useEffect(() => {
    if (!item.reviewCategoryId || item.variants.length === 0) return;
    getCategoryAttributes(item.reviewCategoryId)
      .then((attributes) => setVariantAttributes(attributes.filter((a) => a.isVariant)))
      .catch(() => setVariantAttributesError("Could not load this category's variant attributes."));
  }, [item.reviewCategoryId, item.variants.length]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Product Details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" value={reviewName} onChange={(e) => setReviewName(e.target.value)} fullWidth autoFocus />
          <TextField
            label="Description"
            value={reviewDescription}
            onChange={(e) => setReviewDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Price"
              type="number"
              value={reviewPrice}
              onChange={(e) => setReviewPrice(e.target.value)}
              fullWidth
            />
            <TextField
              label="Stock Quantity"
              type="number"
              value={reviewStockQuantity}
              onChange={(e) => setReviewStockQuantity(e.target.value)}
              fullWidth
            />
          </Stack>

          {item.images.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Images to include
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {item.images.map((image) => (
                  <Box key={image.productImportItemImageId} sx={{ position: "relative" }}>
                    <Avatar
                      variant="rounded"
                      src={image.externalImageUrl}
                      sx={{
                        width: 64,
                        height: 64,
                        opacity: selectedImageIds.includes(image.productImportItemImageId) ? 1 : 0.35,
                        cursor: "pointer",
                      }}
                      onClick={() => toggleImage(image.productImportItemImageId)}
                    />
                    <Checkbox
                      size="small"
                      checked={selectedImageIds.includes(image.productImportItemImageId)}
                      onChange={() => toggleImage(image.productImportItemImageId)}
                      sx={{ position: "absolute", top: -8, right: -8, bgcolor: "background.paper", p: 0.25 }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {item.extractedVideoUrl && (
            <Box>
              <FormControlLabel
                control={<Switch checked={includeVideo} onChange={(e) => setIncludeVideo(e.target.checked)} />}
                label="Include the video found on the source page"
              />
              {includeVideo && (
                <video
                  src={item.extractedVideoUrl}
                  controls
                  muted
                  style={{ width: "100%", maxHeight: 200, borderRadius: 4, background: "#000" }}
                />
              )}
            </Box>
          )}

          {item.variants.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Variants detected on the source page
              </Typography>
              {!item.reviewCategoryId && (
                <Alert severity="warning">Pick a category above, then save, to map these variants.</Alert>
              )}
              {variantAttributesError && <Alert severity="error">{variantAttributesError}</Alert>}
              {item.reviewCategoryId && variantAttributes.length === 0 && !variantAttributesError && (
                <Alert severity="warning">
                  This category has no variant-generating attributes set up. Add one under Admin → Attributes/Categories first.
                </Alert>
              )}
              {item.reviewCategoryId && variantAttributes.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Detected on source page</TableCell>
                      <TableCell>Attribute</TableCell>
                      <TableCell>Option</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Stock</TableCell>
                      <TableCell align="right">Save</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {item.variants.map((variant) => (
                      <VariantMappingRow
                        key={variant.productImportItemVariantId}
                        variant={variant}
                        variantAttributes={variantAttributes}
                        onSave={(patch) => onSaveVariant(variant.productImportItemVariantId, patch)}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() =>
            onSave(item.productImportItemId, {
              reviewName: reviewName.trim(),
              reviewDescription: reviewDescription.trim() || null,
              reviewPrice: reviewPrice === "" ? null : Number(reviewPrice),
              reviewCategoryId: item.reviewCategoryId ?? null,
              reviewStockQuantity: reviewStockQuantity === "" ? null : Number(reviewStockQuantity),
              selectedImageIds,
              includeVideo,
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function VariantMappingRow({ variant, variantAttributes, onSave }) {
  const [attributeId, setAttributeId] = useState(variant.reviewAttributeId ?? "");
  const [optionId, setOptionId] = useState(variant.reviewOptionId ?? "");
  const [price, setPrice] = useState(variant.reviewPrice ?? "");
  const [stock, setStock] = useState(variant.reviewStockQuantity ?? "");
  const [saving, setSaving] = useState(false);

  const selectedAttribute = variantAttributes.find((a) => a.attributeId === Number(attributeId));
  const isCreated = Boolean(variant.createdVariantId);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave({
        reviewAttributeId: attributeId === "" ? null : Number(attributeId),
        reviewOptionId: optionId === "" ? null : Number(optionId),
        reviewPrice: price === "" ? null : Number(price),
        reviewStockQuantity: stock === "" ? null : Number(stock),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <TableRow>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          {variant.externalImageUrl && <Avatar variant="rounded" src={variant.externalImageUrl} sx={{ width: 32, height: 32 }} />}
          <Box>
            <Typography variant="body2">{variant.optionLabelRaw}</Typography>
            <Typography variant="caption" color="text.secondary">
              {variant.attributeNameRaw}
              {variant.extractedStockQuantity != null ? ` · stock ${variant.extractedStockQuantity}` : ""}
            </Typography>
          </Box>
        </Stack>
        {variant.errorMessage && (
          <Typography variant="caption" color="error" component="div">
            {variant.errorMessage}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 140 }}>
        <TextField
          select
          size="small"
          fullWidth
          disabled={isCreated}
          value={attributeId}
          onChange={(e) => {
            setAttributeId(e.target.value);
            setOptionId("");
          }}
        >
          {variantAttributes.map((a) => (
            <MenuItem key={a.attributeId} value={a.attributeId}>
              {a.attributeName}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell sx={{ minWidth: 140 }}>
        <TextField
          select
          size="small"
          fullWidth
          disabled={isCreated || !selectedAttribute}
          value={optionId}
          onChange={(e) => setOptionId(e.target.value)}
        >
          {(selectedAttribute?.options || []).map((o) => (
            <MenuItem key={o.optionId} value={o.optionId}>
              {o.value}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell sx={{ width: 100 }}>
        <TextField
          type="number"
          size="small"
          fullWidth
          disabled={isCreated}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
      </TableCell>
      <TableCell sx={{ width: 90 }}>
        <TextField
          type="number"
          size="small"
          fullWidth
          disabled={isCreated}
          value={stock}
          onChange={(e) => setStock(e.target.value)}
        />
      </TableCell>
      <TableCell align="right">
        {isCreated ? (
          <Chip size="small" color="success" label="Created" />
        ) : (
          <Button size="small" disabled={saving || !attributeId || !optionId} onClick={handleSave}>
            Save
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
